import * as React from 'react';
import { useHistory, Redirect } from 'react-router-dom';
import Modal from '../layout/modal/Modal';
import ModalHeader from '../layout/modal/ModalHeader';
import ProjectTable from './ProjectTable';
import { useMsgText } from '../localization/hooks';
import { Str } from '../localization/translationKeys';
import { ActionType } from '../core/redux/actionType';
import { useLoading, useError } from '../core/api/hooks';
import { useMount } from '../core/hooks/useMount';
import { useDispatch, useSelector } from 'react-redux';
import { loadProjectsAction } from './loadProjectsAction';
import { AppState } from '../core/redux/appState';
import { useRoutes, makeRoutes } from '../navigation/routes';
import { logOutAction } from '../auth/login/api/logoutAction';
import { useAppState } from '../core/redux/hooks';
import { useWindowSize } from '../core/hooks/useWindowSize';


type Props = {
  onDismiss?: () => void,
  className?: string;
};


/**
 * ProjectModal: a component that renders a modal listing the user's projects and links to those projects. The user can
 * select a link to view the dashboard for that project.
 *
 * PureComponent: true
 * Redux Connected: true
 */
const ProjectModal: React.FC<Props> = props => {
  const dispatch = useDispatch();
  const history  = useHistory();

  const header = useMsgText(Str.ChooseProject);

  const { isMobile } = useWindowSize();

  const isLoading = useLoading(ActionType.LoadProjectsRequest);
  const error     = useError(ActionType.LoadProjectsRequest);
  const projects  = useAppState(state => state.projects);
  const project   = useAppState(state => state.projectSummary);

  const hasProjects   = !!projects?.length;
  const hasOneProject = !!projects && projects?.length === 1;

  const personId = useSelector<AppState, number>(state => state.auth.currentUser?.personId!);

  useMount(() => {
    if (!projects) {
      dispatch(loadProjectsAction(personId));
    }
  });

  const routes = useRoutes(personId, projects?.[0]?.displayProjectId ?? '');

  const dismiss = React.useMemo(
    () => props.onDismiss ?? (
      hasProjects
        ? () => {
          // If we're already on an active project, and the user is dismissing the modal, just stay on the current proj
          if (project) {
            history.push(makeRoutes(personId, project.displayProjectId).Project);
            return;
          }

          history.replace(routes.Project);
        }
        : () => {
          dispatch(logOutAction(isMobile));
          history.replace(routes.Login);
        }
    ),
    [hasProjects, props.onDismiss, history, dispatch, routes, isMobile, personId, project],
  );

  return (
    hasOneProject
      ? <Redirect to={routes.Project} />
      : (
        <Modal onDismiss={dismiss} dismissable={true}>
          <ModalHeader title={header} onDismiss={dismiss} />
          <ProjectTable
            loading={isLoading}
            error={!!error}
            projects={projects}
          />
        </Modal>
      )
  );
};

export default ProjectModal;
